import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import React from 'react'
import { match } from 'ts-pattern'
import {
  Repository,
  Scale,
  ToolSchema,
  type UpdateWorkflowRequest,
  type WorkflowResponse,
} from '../../utils/api-client/api-schemas.ts'
import { getToolName } from '../../utils/repository-utils.tsx'
import { getSeveritiesByRankDescending } from '../../utils/scale-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { InputContainer } from '../input/input-container'
import { SelectBox } from '../input/select-box.tsx'
import { Slider } from '../input/slider'
import { TextInput } from '../input/text-input'
import { Switch } from '../switch.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import * as styles from './add-findings-drawer.css'
import * as localStyles from './workflow-drawer.css'
import {
  BranchPrefixInput,
  FixesCustomizationSelector,
  isFixConfidence,
  SeveritySelector,
  TOOLS_SHOWING_SLIDER,
} from './workflow-form-controls.tsx'
import * as formStyles from './workflow-form.css'

// #region Form factory

type FixConfidenceValue = 'high' | 'medium' | 'low' | 'no-rating'

export type EditWorkflowFormValues = {
  name: string
  enabled: boolean
  cadence: 'daily' | 'weekly'
  branch: string
  targetBranch: string
  actionType: 'create-patch' | 'none'
  selectedSeverities: string[]
  minimumSeverityScore: number
  maximumSeverityScore: number
  fixConfidence: FixConfidenceValue | undefined
  limitFixes: boolean
  fixesLimit: number
}

// Used only for typing the consumer-side context hook; actual values come from
// the upstream `useEditWorkflowForm(workflow)` call.
const EDIT_FORM_VALUES_PLACEHOLDER: EditWorkflowFormValues = {
  name: '',
  enabled: true,
  cadence: 'daily',
  branch: '',
  targetBranch: '',
  actionType: 'none',
  selectedSeverities: [],
  minimumSeverityScore: 0,
  maximumSeverityScore: 10,
  fixConfidence: undefined,
  limitFixes: false,
  fixesLimit: 1,
}

const { fieldContext, formContext } = createFormHookContexts()
const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

export const useEditWorkflowForm = (workflow: WorkflowResponse) =>
  useAppForm({ defaultValues: workflowToFormValues(workflow) })

export type EditWorkflowFormType = ReturnType<typeof useEditWorkflowForm>

const useFormContext = () => useTypedAppFormContext({ defaultValues: EDIT_FORM_VALUES_PLACEHOLDER })

// #endregion

const WORKFLOW_TYPE_OPTIONS = [
  { value: 'schedule', label: 'Schedule fixes' },
  { value: 'new-scan', label: 'Analyze new scans' },
  { value: 'pull-request-scan', label: 'Analyze pull requests' },
] as const

export type EditWorkflowFormProps = {
  workflow: WorkflowResponse
  repositoryType: Repository['type']
  repositoryDisplayName: string
  scaleItems: Scale[]
}

export const EditWorkflowForm: React.FC<EditWorkflowFormProps> = ({
  workflow,
  repositoryType,
  repositoryDisplayName,
  scaleItems,
}) => {
  const form = useFormContext()
  const name = useSelector(form.store, state => state.values.name)
  const enabled = useSelector(form.store, state => state.values.enabled)
  const cadence = useSelector(form.store, state => state.values.cadence)
  const branch = useSelector(form.store, state => state.values.branch)
  const targetBranch = useSelector(form.store, state => state.values.targetBranch)
  const actionType = useSelector(form.store, state => state.values.actionType)
  const selectedSeverities = useSelector(form.store, state => state.values.selectedSeverities)
  const minimumSeverityScore = useSelector(form.store, state => state.values.minimumSeverityScore)
  const maximumSeverityScore = useSelector(form.store, state => state.values.maximumSeverityScore)
  const fixConfidence = useSelector(form.store, state => state.values.fixConfidence)
  const limitFixes = useSelector(form.store, state => state.values.limitFixes)
  const fixesLimit = useSelector(form.store, state => state.values.fixesLimit)

  const showSlider = TOOLS_SHOWING_SLIDER.includes(workflow.tool)

  const toolScale = match(workflow.tool)
    .with('appscan', () => scaleItems.find(scale => scale.id === 'appscan'))
    .with('veracode', () => scaleItems.find(scale => scale.id === 'veracode'))
    .with('semgrep', 'snyk', () => scaleItems.find(scale => scale.id === 'sarif-levels'))
    .otherwise(() => scaleItems.find(scale => scale.id === 'sonar-merged'))
  const toolSeverities = toolScale?.type === 'ranked-set' ? getSeveritiesByRankDescending(toolScale) : undefined

  return (
    <>
      <Dialog.Title className={`${styles.title} ${localStyles.title}`}>
        <div className={localStyles.editTitleRepo}>
          <SCMIcon type={repositoryType} size="medium" />
          <div className={localStyles.repoName}>{repositoryDisplayName}</div>
        </div>
        Edit workflow
      </Dialog.Title>
      <VisuallyHidden>
        <Dialog.Description>Edit workflow configuration</Dialog.Description>
      </VisuallyHidden>

      <div className={localStyles.editFormContainer}>
        <div className={localStyles.editFormNameSection}>
          <div className={styles.flexContainer}>
            <TextInput
              id="edit-workflow-name"
              name="name"
              label="Name"
              value={name}
              onChange={value => form.setFieldValue('name', value)}
              placeholder="Override default workflow name"
            />
          </div>

          <label className={localStyles.enabledSwitchRow}>
            <Switch
              checked={enabled}
              onCheckedChange={checked => form.setFieldValue('enabled', checked)}
              aria-label={enabled ? 'Workflow enabled' : 'Workflow disabled'}
            />
            <span className={localStyles.enabledSwitchLabel}>Workflow {enabled ? 'ENABLED' : 'DISABLED'}</span>
          </label>

          <hr className={localStyles.editFormDivider} />
        </div>

        <SelectBox
          label="Workflow"
          name="workflowType"
          disabled
          value={workflow.event.type}
          onValueChange={() => {}}
          options={WORKFLOW_TYPE_OPTIONS.map(option => ({ ...option }))}
        />

        <SelectBox
          label="Tool"
          name="tool"
          disabled
          value={workflow.tool}
          onValueChange={() => {}}
          options={ToolSchema.options.map(tool => ({
            value: tool,
            label: (
              <div className={formStyles.toolOptionLabel}>
                <ToolIcon tool={tool} size="medium" />
                {getToolName(tool)}
              </div>
            ),
          }))}
        />

        {(workflow.event.type === 'schedule' || workflow.event.type === 'new-scan') && (
          <BranchPrefixInput branch={branch} setBranch={value => form.setFieldValue('branch', value)} />
        )}

        {workflow.event.type === 'pull-request-scan' && (
          <BranchPrefixInput
            branch={targetBranch}
            setBranch={value => form.setFieldValue('targetBranch', value)}
            helperText="Target branch for PR matching. Use * for prefix patterns."
          />
        )}

        {(workflow.event.type === 'new-scan' || workflow.event.type === 'pull-request-scan') && (
          <SelectBox
            label="Action"
            disabled
            value={actionType}
            onValueChange={() => {}}
            options={[
              { value: 'none', label: 'Analyze only' },
              { value: 'create-patch', label: 'Create patch' },
            ]}
          />
        )}

        {actionType === 'create-patch' && (
          <FixesCustomizationSelector
            severitySection={
              <>
                {toolSeverities && !showSlider && (
                  <SeveritySelector
                    severities={toolSeverities}
                    selectedSeverities={selectedSeverities}
                    setSelectedSeverities={updater => {
                      const next = typeof updater === 'function' ? updater(selectedSeverities) : updater
                      form.setFieldValue('selectedSeverities', next)
                    }}
                  />
                )}
                {showSlider && (
                  <InputContainer label="Severity score range" explicitLabelCondition="optional">
                    <Slider
                      value={[minimumSeverityScore, maximumSeverityScore]}
                      onValueChange={([min, max]) => {
                        form.setFieldValue('minimumSeverityScore', min)
                        form.setFieldValue('maximumSeverityScore', max)
                      }}
                      min={0}
                      max={10}
                      step={0.1}
                    />
                  </InputContainer>
                )}
              </>
            }
            fixConfidence={isFixConfidence(fixConfidence) ? fixConfidence : undefined}
            setFixConfidence={updater => {
              const current = isFixConfidence(fixConfidence) ? fixConfidence : undefined
              const next = typeof updater === 'function' ? updater(current) : updater
              form.setFieldValue('fixConfidence', next)
            }}
            limitFixes={limitFixes}
            setLimitFixes={updater => {
              const next = typeof updater === 'function' ? updater(limitFixes) : updater
              form.setFieldValue('limitFixes', next)
            }}
            fixesLimit={fixesLimit}
            setFixesLimit={updater => {
              const next = typeof updater === 'function' ? updater(fixesLimit) : updater
              form.setFieldValue('fixesLimit', next)
            }}
          />
        )}

        {workflow.event.type === 'schedule' && (
          <SelectBox
            label="Cadence"
            value={cadence}
            onValueChange={value => form.setFieldValue('cadence', value as 'daily' | 'weekly')}
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
            ]}
          />
        )}
      </div>
    </>
  )
}

export type EditWorkflowSaveButtonProps = {
  workflow: WorkflowResponse
  isUpdating: boolean
  onSave: (update: UpdateWorkflowRequest) => unknown
}

export const EditWorkflowSaveButton: React.FC<EditWorkflowSaveButtonProps> = ({ workflow, isUpdating, onSave }) => {
  const form = useFormContext()
  // Use !isDefaultValue (top-level form state) rather than isDirty: TanStack's `isDirty`
  // flips on any mutation, even ones that set a field back to its default value. We want
  // "is any field different from its initial value?", which is the opposite of isDefaultValue.
  const isDirty = useSelector(form.store, state => !state.isDefaultValue)

  return (
    <DefaultButton
      buttonType="primary"
      state={isDirty && !isUpdating ? 'default' : 'disabled'}
      buttonRole="submit"
      onClick={() => {
        const update = buildUpdateRequestFromDirtyFields(form, workflow)
        if (update) {
          onSave(update)
        }
      }}
      data-testid="workflow-drawer-save-workflow-button"
    >
      {isUpdating ? 'Saving…' : 'Save changes'}
    </DefaultButton>
  )
}

const workflowToFormValues = (workflow: WorkflowResponse): EditWorkflowFormValues => {
  const actionValues = match(workflow.action)
    .with({ type: 'create-patch' }, action => {
      const findingCountLimit = typeof action.limit === 'object' ? action.limit : undefined
      return {
        actionType: 'create-patch' as const,
        selectedSeverities: action.findings?.include_severity_labels ?? [],
        minimumSeverityScore: action.findings?.minimum_severity_score ?? 0,
        maximumSeverityScore: action.findings?.maximum_severity_score ?? 10,
        fixConfidence: action.fixes?.minimum_fix_confidence,
        limitFixes: findingCountLimit !== undefined,
        fixesLimit: findingCountLimit?.max ?? 1,
      }
    })
    .with({ type: 'none' }, () => ({
      actionType: 'none' as const,
      selectedSeverities: [] as string[],
      minimumSeverityScore: 0,
      maximumSeverityScore: 10,
      fixConfidence: undefined,
      limitFixes: false,
      fixesLimit: 1,
    }))
    .exhaustive()

  const eventValues = match(workflow.event)
    .with({ type: 'schedule' }, event => ({
      cadence: event.cadence,
      branch: event.branch ?? '',
      targetBranch: '',
    }))
    .with({ type: 'new-scan' }, event => ({
      cadence: 'daily' as const,
      branch: event.branch ?? '',
      targetBranch: '',
    }))
    .with({ type: 'pull-request-scan' }, event => ({
      cadence: 'daily' as const,
      branch: '',
      targetBranch: event.target_branch ?? '',
    }))
    .exhaustive()

  return {
    name: workflow.name ?? '',
    enabled: workflow.enabled,
    ...eventValues,
    ...actionValues,
  }
}

/**
 * Builds the merge-semantics PUT payload for the workflow update endpoint.
 *
 * Reads the form via TanStack Form's typed accessors (`getFieldValue` /
 * `getFieldMeta`), includes only the fields the user actually changed
 * (`meta.isDefaultValue === false`), and shapes them into the structure
 * expected by `PUT /api/v1/workflows/:id`. Returns `undefined` when no
 * field is dirty so the caller can no-op the request.
 */
const buildUpdateRequestFromDirtyFields = (
  form: EditWorkflowFormType,
  workflow: WorkflowResponse
): UpdateWorkflowRequest | undefined => {
  const isDirty = (field: keyof EditWorkflowFormValues) => form.getFieldMeta(field)?.isDefaultValue === false

  const update: UpdateWorkflowRequest = {}
  let hasDirtyFields = false

  if (isDirty('name')) {
    update.name = form.getFieldValue('name') || undefined
    hasDirtyFields = true
  }

  if (isDirty('enabled')) {
    update.enabled = form.getFieldValue('enabled')
    hasDirtyFields = true
  }

  const eventDirtyFields = match(workflow.event.type)
    .with('schedule', () => {
      const dirtyEvent: Record<string, unknown> = {}
      let hasEventDirty = false
      if (isDirty('cadence')) {
        dirtyEvent.cadence = form.getFieldValue('cadence')
        hasEventDirty = true
      }
      if (isDirty('branch')) {
        dirtyEvent.branch = form.getFieldValue('branch') || undefined
        hasEventDirty = true
      }
      return hasEventDirty ? { type: 'schedule' as const, ...dirtyEvent } : undefined
    })
    .with('new-scan', () => {
      if (isDirty('branch')) {
        return { type: 'new-scan' as const, branch: form.getFieldValue('branch') || undefined }
      }
      return undefined
    })
    .with('pull-request-scan', () => {
      if (isDirty('targetBranch')) {
        return {
          type: 'pull-request-scan' as const,
          target_branch: form.getFieldValue('targetBranch') || undefined,
        }
      }
      return undefined
    })
    .exhaustive()

  if (eventDirtyFields) {
    update.event = eventDirtyFields
    hasDirtyFields = true
  }

  const actionDirtyFields = (() => {
    const actionType = isDirty('actionType') ? form.getFieldValue('actionType') : workflow.action.type
    if (actionType === 'none') {
      if (isDirty('actionType')) {
        return { type: 'none' as const }
      }
      return undefined
    }
    const dirtyAction: Record<string, unknown> = {}
    let hasActionDirty = isDirty('actionType')
    if (isDirty('selectedSeverities') || isDirty('minimumSeverityScore') || isDirty('maximumSeverityScore')) {
      const findings: {
        include_severity_labels?: string[] | null
        minimum_severity_score?: number
        maximum_severity_score?: number
      } = {}
      if (isDirty('selectedSeverities')) {
        // Spec: `null` clears the labels. Send the array when non-empty, `null` when the
        // user has cleared all selections (dirty + empty).
        const selectedSeverities = form.getFieldValue('selectedSeverities')
        findings.include_severity_labels = selectedSeverities.length > 0 ? selectedSeverities : null
      }
      if (isDirty('minimumSeverityScore')) {
        findings.minimum_severity_score = form.getFieldValue('minimumSeverityScore')
      }
      if (isDirty('maximumSeverityScore')) {
        findings.maximum_severity_score = form.getFieldValue('maximumSeverityScore')
      }
      dirtyAction.findings = findings
      hasActionDirty = true
    }
    if (isDirty('fixConfidence')) {
      const fixConfidence = form.getFieldValue('fixConfidence')
      dirtyAction.fixes = fixConfidence ? { minimum_fix_confidence: fixConfidence } : null
      hasActionDirty = true
    }
    if (isDirty('limitFixes') || isDirty('fixesLimit')) {
      const limitFixes = form.getFieldValue('limitFixes')
      const fixesLimit = form.getFieldValue('fixesLimit')
      dirtyAction.limit = limitFixes ? { type: 'finding-count', max: fixesLimit } : null
      hasActionDirty = true
    }
    return hasActionDirty ? { type: 'create-patch' as const, ...dirtyAction } : undefined
  })()

  if (actionDirtyFields) {
    update.action = actionDirtyFields
    hasDirtyFields = true
  }

  return hasDirtyFields ? update : undefined
}
